/**
 * System API Module - Health, Audit, Export, and Utilities
 * Extracted from monolithic silo-api.ts for modular architecture
 */

import { type RequestFunction } from './types'
import type {
  User,
  PaginatedResponse,
  FilterParams,
} from './types'

export class SystemApi {
  constructor(private request: RequestFunction) {}

  // ============================================================================
  // SYSTEM HEALTH
  // ============================================================================

  async getSystemHealth(): Promise<{
    status: 'healthy' | 'degraded' | 'down'
    database: boolean
    cache: boolean
    storage: boolean
    api_latency: number
  }> {
    return this.request('/api/system/health/')
  }

  async getSystemHealthAdmin(): Promise<{
    status: 'healthy' | 'warning' | 'error'
    checks: Array<{
      name: string
      status: 'pass' | 'fail' | 'warning'
      message?: string
    }>
  }> {
    return this.request('/api/system/health/')
  }

  // ============================================================================
  // AUDIT LOGS
  // ============================================================================

  async getAuditLogs(params?: {
    user_id?: number
    entity_type?: string
    entity_id?: number
    action?: string
    start_date?: string
    end_date?: string
    page?: number
    limit?: number
  }): Promise<PaginatedResponse<{
    id: number
    user: User
    action: string
    entity_type: string
    entity_id: number
    changes: Record<string, any>
    created_at: string
  }>> {
    return this.request('/api/audit-logs/', { params })
  }

  async getAuditLogsAdmin(params?: {
    page?: number
    limit?: number
    user_id?: number
    action?: string
    date_from?: string
    date_to?: string
  }): Promise<PaginatedResponse<{
    id: number
    user: User
    action: string
    model: string
    object_id: string
    changes: Record<string, any>
    timestamp: string
  }>> {
    return this.request('/api/system/audit-logs/', { params })
  }

  // ============================================================================
  // DATA EXPORT
  // ============================================================================

  async exportData(
    entityType: 'contacts' | 'products' | 'orders',
    format: 'csv' | 'json' | 'xlsx' = 'csv'
  ): Promise<Blob> {
    return this.request(`/api/export/${entityType}/`, { params: { format } })
  }

  async exportDataAdmin(type: 'contacts' | 'orders' | 'inventory', format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    return this.request(`/api/system/export/${type}/`, { params: { format } })
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Utility method to detect if user is staff based on response fields
   */
  isStaffUser(response: any): boolean {
    // Check for staff-only fields
    return (
      response?.cost !== undefined ||
      response?.margin !== undefined ||
      response?.internal_notes !== undefined ||
      response?.profit !== undefined ||
      response?.supplier_info !== undefined
    )
  }

  /**
   * Helper to detect available fields for role-based UI
   */
  detectAvailableFields(data: any[]): Set<string> {
    const fields = new Set<string>()
    if (data.length > 0) {
      Object.keys(data[0]).forEach(key => fields.add(key))
    }
    return fields
  }
}